import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { createTheme } from '@mui/material/styles';
import { useMediaQuery, useTheme } from '@mui/material';
import { useMobileLayout } from '../../contexts/MobileLayoutContext';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { primitiveColors, semanticColors, typographyTokens } from '../../theme/tokens';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import type { Navigation } from '@toolpad/core/AppProvider';
import { Branding } from './Branding';
import { useAuth } from '../auth/AuthProvider';
import { apiClient } from '@/utils/apiClient';
import { ReactRouterAppProvider } from '@toolpad/core/react-router';
import SampleBallotView from '../ballot/SampleBallotView';
import BallotMenu from '../ballot/BallotMenu';
import BallotSheet from '../ballot/BallotSheet';
import SafeAreaFiller from '../ballot/SafeAreaFiller';
import CanvasBackground from '../ballot/CanvasBackground';
import MobileAddressBar from '../ballot/MobileAddressBar';
import MobileBallotPanel from '../ballot/MobileBallotPanel';
import MobileTabBar from '../ballot/MobileTabBar';
import GuestLandingPage from '../landing/GuestLandingPage';
import useGuestAddress from '@/hooks/useGuestAddress';
import useGuestBallotDownload from '@/hooks/useGuestBallotDownload';
import { useBallotRecords } from '@/contexts/BallotRecordsContext';
import { ContextService } from '@/services/contextService';
import useAnalytics from '@/hooks/useAnalytics';
import { BallotSheetProvider, MOBILE_TOOLBAR_HEIGHT_PX } from '@/providers/BallotSheetProvider';
import { useNavMode } from '@/hooks/useNavMode';
import FeedbackSurveyChip from '../feedback-survey/FeedbackSurveyChip';
import { useSurveyContext } from '@/contexts/SurveyContext';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { HeaderLanguageSelector } from './HeaderLanguageSelector';
import { HeaderAccountMenu } from './HeaderAccountMenu';
import { RailHoverContext } from './RailHoverContext';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/hooks/useDirection';

const NAVIGATION: Navigation = [];
const RIGHT_BAR_WIDTH = '16px';

const demoTheme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data-toolpad-color-scheme',
  },
  colorSchemes: { light: true },
  typography: {
    fontFamily: typographyTokens.fontFamily,
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: typographyTokens.fontFamily,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: typographyTokens.fontFamily,
          textTransform: 'none' as const,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: typographyTokens.fontFamily,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 1200,
      xl: 1536,
    },
  },
  mixins: {
    toolbar: {
      // 48px toolbar with no border (the sx override below disables
      // ``borderBottom``). The previous 52 included a 4px buffer for a
      // border that no longer exists, causing every consumer of this
      // mixin to leave a phantom 4px gap below the app bar.
      minHeight: 48,
    },
  },
});

function CustomToolbarAccount({
  onSignIn,
  onFeedbackClick,
}: {
  onSignIn: () => void;
  onFeedbackClick?: () => void;
}) {
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  if (isAuthenticated && user) {
    // Signed in: the phone layout gets an avatar menu in the corner, where the
    // desktop layout's rail avatar (bottom-left) isn't available.
    return isMobile ? <HeaderAccountMenu onFeedbackClick={onFeedbackClick} /> : null;
  }
  return (
    // Solid dark pill CTA: high-contrast white text on the brand's
    // ``rockSlate`` surface (~13:1, comfortably WCAG AA), a fully
    // rounded shape, and a subtle lift on hover for a modern feel.
    <Button
      disableRipple
      onClick={onSignIn}
      size="small"
      sx={{
        textTransform: 'none',
        borderRadius: '999px',
        // Breathing room so the pill doesn't hug the top-right corner of
        // the app bar on the full-width chat page. Doubled specificity
        // (``&&``) beats the Toolpad toolbar Stack's child-margin reset.
        '&&': {
          marginTop: { xs: '6px', md: '8px' },
          marginRight: { xs: '8px', md: '16px' },
        },
        px: { xs: 1.25, md: 2.25 },
        py: 0.5,
        fontSize: { xs: '11px', md: '13px' },
        whiteSpace: 'nowrap',
        fontWeight: 600,
        letterSpacing: '0.01em',
        backgroundColor: semanticColors.textPrimary,
        color: semanticColors.textOnPrimary,
        minHeight: '32px',
        boxShadow: 'none',
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: semanticColors.brandAccent,
          boxShadow: 'none',
        },
        '&:active': {
          boxShadow: 'none',
        },
        '&:focus-visible': {
          outline: `2px solid ${semanticColors.brandPrimary}`,
          outlineOffset: '2px',
        },
      }}
    >
      {t('signInSignUp')}
    </Button>
  );
}

// Custom toolbar actions. Feedback now lives in the left-pane menu, so the
// toolbar only carries the language selector.
function CustomToolbarActions() {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <HeaderLanguageSelector />
    </Stack>
  );
}

interface DashboardLayoutProps {
  /**
   * Injected by the documentation to work in an iframe.
   * Remove this when copying and pasting into your project.
   */
  window?: () => Window;

  children?: React.ReactNode;

  // Optional props for layout customization
  showWhiteStrip?: boolean;
  onMenuClick?: () => void;
  guestMode?: boolean; // New prop to control guest mode
  hideAccountButton?: boolean; // Hide the top-right sign-in/account button
  pageScroll?: boolean; // Use window/page scroll instead of fixed-height inner scroll

  // Ballot-related props
  ballotOpen?: boolean;
  onToggleBallot?: () => void;

  // Handles a click on the rail's account avatar. Signed-in screens supply this
  // to slide the left menu out and expand its options accordion — the single
  // entry point for account actions.
  onAccountIconClick?: () => void;

  // When true, hides the strip account glyph so the drawer's own gradient border
  // renders in its place without doubling up.
  menuOpen?: boolean;

  // When true, hides the branding logo in the nav toolbar (e.g. on the chat landing state)
  hideLogo?: boolean;

  // Controls whether the floating feedback survey chip renders. Defaults to true.
  showFeedbackChip?: boolean;

  // Override for the header account menu's Feedback action (mobile). Pages that
  // embed the feedback form (Help) scroll to it instead of opening the survey panel.
  onFeedbackClick?: () => void;
}

export const DashboardLayoutAccountSidebar = (props: DashboardLayoutProps) => {
  const {
    window,
    children,
    showWhiteStrip = true,
    onMenuClick,
    guestMode = false,
    hideAccountButton = false,
    pageScroll = false,
    ballotOpen = false,
    onToggleBallot,
    onAccountIconClick,
    menuOpen = false,
    hideLogo = false,
    showFeedbackChip = true,
    onFeedbackClick,
  } = props;
  const { user, signOut, isAuthenticated, signIn } = useAuth();
  const { track } = useAnalytics();
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dir = useDirection();

  // Get mobile layout context for mobile ballot control
  const mobileLayout = useMobileLayout() || { ballotOpen: false };
  const mobileBallotOpen = mobileLayout.ballotOpen;

  // Use different ballot state for mobile vs desktop
  const effectiveBallotOpen = isMobile ? mobileBallotOpen : ballotOpen;

  const { openSurveyPanel } = useSurveyContext();

  const handleSignInClick = () => {
    track('Header Sign In Clicked');
    signIn();
  };

  // Hovering the rail strip recolors the drawer's account avatar, which lives
  // in a separate portal subtree and can't be reached by the rail's `:hover`.
  const [railHovered, setRailHovered] = React.useState(false);
  const [isBallotDownloading, setIsBallotDownloading] = React.useState(false);
  const ballotDownloadRef = React.useRef<(() => void) | null>(null);

  // Service notification banner
  const [notificationMessage, setNotificationMessage] = React.useState('');
  const [notificationHeight, setNotificationHeight] = React.useState(0);
  const notificationRef = React.useRef<HTMLDivElement>(null);

  // Hits the frontend Express server (baseUrl: '') rather than the backend API
  React.useEffect(() => {
    apiClient('/api/service-notification', { baseUrl: '' })
      .then((res) => res.json())
      .then((data: { message: string }) => setNotificationMessage(data.message ?? ''))
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!notificationRef.current) {
      setNotificationHeight(0);
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setNotificationHeight(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
      }
    });
    observer.observe(notificationRef.current);
    return () => observer.disconnect();
  }, [notificationMessage]);

  const hasNotification = Boolean(notificationMessage);
  const bannerHeight = notificationHeight;

  // Determine if ballot should be accessible (authenticated AND not in guest mode)
  const shouldShowBallot = isAuthenticated && !guestMode;
  const hasCompletedBallotSetup = Boolean(user?.initialSetupComplete);
  const canLoadRealBallot = shouldShowBallot && hasCompletedBallotSetup;
  const navMode = useNavMode();
  // Signup (name/address) modal is up: authenticated but setup not done. The
  // ballot sheet sits above the modal (zIndex modal + 1) and has no data yet, so
  // stand it down — the sheet-mode counterpart to #2550's tab-bar hide.
  const signupPending = isAuthenticated && Boolean(user) && !hasCompletedBallotSetup;

  // Desktop right-pane swap: when an unauthenticated user with no
  // cached address is on the chat, replace the national-ballot
  // snapshot view with the guest landing page UI (embedded variant).
  // The landing's address autocomplete writes through
  // ``useGuestAddress``; once a guest address is set,
  // ``showGuestLandingInPane`` flips to false and this conditional
  // re-renders the panel with the personalized ballot. Desktop-only;
  // on mobile the equivalent flow is the BallotSheet / MobileAddressBar.
  const { address: guestAddress } = useGuestAddress();
  const hasGuestAddress = Boolean(guestAddress?.formatted_address);
  const guestDownload = useGuestBallotDownload();
  // Signed-in user who deleted their last ballot: show the landing hero, not the
  // national snapshot. ``recordsLoading`` guards the fetch so the hero doesn't
  // flash before records arrive. The profile address stays; chat context is
  // cleared (below) so the assistant won't answer from the removed ballot.
  const {
    records: savedBallotRecords,
    recordsLoading: savedBallotsLoading,
    building: ballotBuilding,
  } = useBallotRecords();
  // ``building`` (a ballot is mid-creation post-signup / post-address-entry)
  // suppresses the landing so the skeleton stays up instead of flashing the hero.
  const noSavedBallots =
    canLoadRealBallot && !savedBallotsLoading && !ballotBuilding && savedBallotRecords.length === 0;
  // Show the landing (address entry) whenever no address exists anywhere and the
  // user isn't past setup: guests with no cached address, and authed users still
  // in signup (behind the modal) who haven't entered one. A cached guest address
  // keeps its ballot visible instead. ``noSavedBallots`` covers the deleted-all case.
  const showGuestLandingInPane =
    (!hasGuestAddress && (!isAuthenticated || !hasCompletedBallotSetup)) || noSavedBallots;
  React.useEffect(() => {
    if (noSavedBallots) ContextService.clearContext();
  }, [noSavedBallots]);

  // Remove this const when copying and pasting into your project.
  const demoWindow = window !== undefined ? window() : undefined;

  // Pass auth state to Toolpad for sign-in button visibility,
  // but omit user.name to prevent the tooltip leak on hover
  const session =
    user && !guestMode && !hideAccountButton ? { user: { name: '', email: '', image: '' } } : null;

  const authentication = React.useMemo(() => {
    return {
      signIn: () => {
        // Redirect to signup page
        signIn();
      },
      signOut: () => {
        signOut();
      },
    };
  }, [signOut, signIn]);

  return (
    // Remove this provider when copying and pasting into your project.
    <ReactRouterAppProvider
      navigation={NAVIGATION}
      theme={demoTheme}
      window={demoWindow}
      authentication={authentication}
      session={session}
      branding={{
        title: '',
        logo: false,
      }}
    >
      {/* Service notification banner */}
      {notificationMessage && (
        <Box
          ref={notificationRef}
          role="status"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            px: 2,
            py: 1,
            backgroundColor: semanticColors.primary,
            borderBottom: `1px solid ${semanticColors.brandPrimary}`,
          }}
        >
          <InfoOutlinedIcon
            aria-hidden="true"
            sx={{ fontSize: 20, color: semanticColors.brandPrimary, flexShrink: 0 }}
          />
          <Typography
            variant="body2"
            sx={{ color: semanticColors.textPrimary, fontWeight: 500, textAlign: 'center' }}
          >
            {notificationMessage}
          </Typography>
        </Box>
      )}
      {/* Mobile: Flex column container. Wrapped in BallotSheetProvider
          so the mobile BallotSheet / Backdrop (rendered below) share
          drag + detent state via context. ``headerOffsetPx`` keeps the
          sheet's full detent and the backdrop's top edge flush with
          the actual app-header bottom — toolbar (48) plus the live
          notification banner height, so the bar moving down on banner
          appear/disappear is followed by the sheet. */}
      <BallotSheetProvider
        headerOffsetPx={MOBILE_TOOLBAR_HEIGHT_PX + bannerHeight}
        ballotMode={canLoadRealBallot ? 'personal' : 'national'}
        navMode={navMode}
        guestLandingActive={showGuestLandingInPane}
      >
        <RailHoverContext.Provider value={railHovered}>
          <Box
            sx={{
              display: {
                xs: 'flex',
                md: 'block',
              },
              flexDirection: {
                xs: 'column',
                md: 'unset',
              },
              // ``dvh`` on mobile so the page root exactly matches the
              // currently-visible viewport. ``vh`` resolves to the
              // URL-bar-hidden viewport, which overflows the visible
              // area on initial load and lets the body scroll that
              // overflow — undesirable on pages with no scrollable
              // content (e.g., chat landing). With ``dvh`` there's no
              // overflow, so swipe gestures fall through to the
              // browser's URL-bar collapse/expand behavior instead.
              height: { xs: pageScroll ? 'auto' : '100dvh', md: 'auto' },
            }}
          >
            {/* preview-start */}
            {/* White strip and hamburger menu - show if enabled */}
            {showWhiteStrip && onMenuClick && (
              <>
                {/* Full-height white strip with hamburger icon */}
                <ButtonBase
                  id="main-menu-button"
                  onClick={onMenuClick}
                  onMouseEnter={() => setRailHovered(true)}
                  onMouseLeave={() => setRailHovered(false)}
                  aria-label={t('openMainMenu')}
                  sx={{
                    position: 'fixed',
                    top: bannerHeight,
                    ...(dir === 'rtl' ? { right: 0, left: 'auto' } : { left: 0 }),
                    width: '35px',
                    height: { xs: '48px', md: `calc(100vh - ${bannerHeight}px)` },
                    // ``railHovered`` (not CSS ``:hover``) drives the hover look so
                    // the sibling account control below — which sits on top of the
                    // strip and would otherwise block the strip's own ``:hover`` —
                    // recolors the strip and hamburger in sync.
                    backgroundColor: {
                      xs: primitiveColors.blue1,
                      md: railHovered ? primitiveColors.gray28 : 'rgb(205, 216, 227)',
                    },
                    // ``theme.zIndex.modal`` puts the strip just below
                    // the BallotSheet (modal + 1) so the sheet's drop
                    // shadow paints over it at full detent — matches
                    // the rest of the app bar visually. Still above the
                    // app bar background (1100) and drawer (1200) so the
                    // hamburger stays tappable in normal use.
                    zIndex: (theme) => theme.zIndex.modal,
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease, color 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    pt: { xs: 0, md: '16px' },
                  }}
                >
                  <Box
                    className="menu-icon"
                    sx={{
                      color: {
                        xs: semanticColors.textBody,
                        md: railHovered ? 'white' : semanticColors.textBody,
                      },
                      backgroundColor: 'transparent',
                      minWidth: '20px',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'color 0.3s ease',
                      pointerEvents: 'none',
                      '& .MuiSvgIcon-root': {
                        fontSize: '18px',
                      },
                    }}
                  >
                    <Bars3Icon width={20} height={20} />
                  </Box>
                </ButtonBase>

                {/* Account control — a sibling of the strip (not nested) so it is
                    its own focusable button rather than a button inside a button
                    (WCAG 4.1.1). Fixed to the bottom of the rail, desktop only.
                    Shares the strip's ``zIndex`` but sits later in the DOM so it
                    paints on top and stays clickable, while remaining below the
                    BallotSheet (modal + 1). */}
                <ButtonBase
                  onClick={() => {
                    if (user && !guestMode) {
                      onAccountIconClick?.();
                    } else {
                      handleSignInClick();
                    }
                  }}
                  onMouseEnter={() => setRailHovered(true)}
                  onMouseLeave={() => setRailHovered(false)}
                  aria-label={user && !guestMode ? t('accountMenu') : t('signInSignUp')}
                  data-settings-return-focus="account"
                  sx={{
                    position: 'fixed',
                    ...(dir === 'rtl' ? { right: 0, left: 'auto' } : { left: 0 }),
                    bottom: '34.5px',
                    width: '35px',
                    height: '41px',
                    zIndex: (theme) => theme.zIndex.modal,
                    // Hidden while the drawer is open — the drawer's account row
                    // renders its own avatar + gradient border there instead, so
                    // the border tracks the row as localized footer links reflow.
                    display: { xs: 'none', md: menuOpen ? 'none' : 'flex' },
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover .account-hover-circle': {
                      backgroundColor: semanticColors.overlayWhite10,
                    },
                  }}
                >
                  <Box
                    className="account-hover-circle"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      backgroundColor: 'transparent',
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    {user && !guestMode ? (
                      <Avatar
                        src={user.picture || undefined}
                        alt={user.name || t('userAvatarAlt')}
                        sx={{ width: 21, height: 21, fontSize: '10px' }}
                      >
                        {user.name?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    ) : (
                      <AccountCircleIcon
                        sx={{
                          width: 21,
                          height: 21,
                          // Recolors with the rail (hover on either the strip or
                          // this control sets ``railHovered``), mirroring the
                          // drawer's portal-rendered avatar.
                          color: railHovered ? 'white' : semanticColors.textTertiary,
                          transition: 'color 0.3s ease',
                        }}
                      />
                    )}
                  </Box>
                </ButtonBase>
              </>
            )}

            <DashboardLayout
              slots={{
                // Stable reference, not an inline arrow: a fresh component type
                // each render makes Toolpad remount the toolbar actions, which
                // tore down the language chip's open dropdown when the ballot
                // sheet's collapse re-rendered the layout (the menu flicker).
                toolbarActions: CustomToolbarActions,
                appTitle: hideLogo ? () => null : () => <Branding logoSize={24} />,
                toolbarAccount: hideAccountButton
                  ? () => null
                  : () => (
                      <CustomToolbarAccount
                        onSignIn={handleSignInClick}
                        onFeedbackClick={onFeedbackClick}
                      />
                    ),
              }}
              disableCollapsibleSidebar={true}
              sx={{
                background: primitiveColors.blue1,
                // pageScroll: constrain the panel to viewport height so
                // MUI Toolpad's internal ``<main flex:1 overflow:auto>``
                // (rendered inside the panel) has bounded space and
                // becomes the scroll container. ``html, body`` are
                // locked (index.html) to prevent keyboard auto-scroll;
                // routing scroll through ``main`` instead keeps the
                // scrollbar visually scoped to the content area and
                // lets the on-screen keyboard scroll only that container
                // when an input is focused, leaving the fixed AppBar
                // pinned.
                ...(pageScroll
                  ? {
                      height: {
                        xs: `calc(100dvh - ${bannerHeight}px) !important`,
                        md: `calc(100vh - ${bannerHeight}px) !important`,
                      },
                      overflow: 'hidden !important',
                    }
                  : {}),
                // ``onToggleBallot`` is the "this screen has a ballot pane"
                // signal. Help and Settings render this layout without it, and
                // between 600 and 899 ``effectiveBallotOpen`` follows the shared
                // mobile state, which rests open — so without this guard those
                // screens yield half their width to a pane that never mounts.
                width: {
                  xs: '100%', // Mobile: full width in flex column
                  md: onToggleBallot && effectiveBallotOpen ? 'calc(50%)' : '100%', // Desktop: main content takes 50% when ballot is open, 100% when closed
                },
                flex: {
                  xs: effectiveBallotOpen ? '0 0 100dvh' : '1 1 auto', // Mobile: dynamic viewport so the panel tracks URL-bar collapse
                  md: 'unset', // Desktop: no flex
                },
                transition: 'all 0.3s ease',
                '& .MuiDrawer-root': {
                  display: 'none',
                },
                '& .MuiAppBar-root': {
                  borderBottom: 'none !important',
                  boxShadow: 'none !important',
                  '& *:not(fieldset)': { borderBottom: 'none !important' },
                  position: 'fixed',
                  ...(dir === 'rtl' ? { left: 'auto', right: 0 } : { right: 'auto' }),
                  // Same guard, and keyed by breakpoint like the content below
                  // it: a scalar here narrowed the bar on the phone layout too,
                  // where the sheet overlays full-width content.
                  width: {
                    xs: '100%',
                    md:
                      onToggleBallot && effectiveBallotOpen
                        ? `calc(50% - ${RIGHT_BAR_WIDTH})`
                        : '100%',
                  },
                  top: bannerHeight,
                },
                ...(showWhiteStrip
                  ? {
                      // White strip layout - offset by 35px for the rail strip
                      '& .MuiBox-root[role="main"]': {
                        ...(dir === 'rtl'
                          ? {
                              marginRight: '35px !important',
                              marginLeft: '0 !important',
                              paddingRight: '8px !important',
                              paddingLeft: '0 !important',
                            }
                          : {
                              marginLeft: '35px !important',
                              paddingLeft: '8px !important',
                            }),
                        width: '100% !important',
                        paddingTop: {
                          // 48px matches the actual rendered toolbar
                          // height (the sx override forces height: 48).
                          // The previous 52 left a 4px phantom gap below
                          // the app bar where chat content was visible
                          // with no overlay.
                          xs: hasNotification
                            ? `calc(48px + ${bannerHeight}px) !important`
                            : '48px !important',
                          md: '0 !important',
                        },
                      },
                      '& main.MuiBox-root': {
                        marginTop: {
                          xs: 0,
                          sm: hasNotification ? `calc(32px + ${bannerHeight}px)` : '32px',
                        },
                      },
                      '& .MuiToolbar-root': {
                        ...(dir === 'rtl'
                          ? {
                              // RTL: rail is on the right, BallotMenu on the left
                              paddingRight: { xs: '48px !important', md: '60px !important' },
                              paddingLeft: {
                                xs: '12px !important',
                                md: effectiveBallotOpen
                                  ? '8px !important'
                                  : onToggleBallot
                                    ? '60px !important'
                                    : `${RIGHT_BAR_WIDTH} !important`,
                              },
                            }
                          : {
                              // LTR: rail is on the left, BallotMenu on the right
                              paddingLeft: { xs: '48px !important', md: '60px !important' },
                              paddingRight: {
                                xs: '12px !important',
                                md: effectiveBallotOpen
                                  ? '8px !important'
                                  : onToggleBallot
                                    ? '60px !important'
                                    : `${RIGHT_BAR_WIDTH} !important`,
                              },
                            }),
                        backgroundColor: `${primitiveColors.blue1} !important`,
                        minHeight: '48px !important',
                        maxHeight: '48px !important',
                        height: '48px !important',
                        paddingTop: '0 !important',
                        paddingBottom: '0 !important',
                        alignItems: 'center !important',
                        display: 'flex !important',
                        borderBottom: 'none !important',
                        boxShadow: 'none !important',
                      },
                    }
                  : {
                      // Standard layout without white strip
                      '& .MuiBox-root[role="main"]': {
                        marginLeft: '0 !important',
                        width: '100% !important',
                        paddingTop: {
                          // 48px matches the actual rendered toolbar
                          // height (the sx override forces height: 48).
                          // The previous 52 left a 4px phantom gap below
                          // the app bar where chat content was visible
                          // with no overlay.
                          xs: hasNotification
                            ? `calc(48px + ${bannerHeight}px) !important`
                            : '48px !important',
                          md: '0 !important',
                        },
                      },
                      '& .MuiToolbar-root': {
                        paddingLeft: { xs: '48px !important', md: '16px !important' },
                        paddingRight: {
                          xs: '12px !important',
                          // See the white-strip branch above: shrink the right
                          // pad when the ballot is open so the account pill sits
                          // 16px from the pane edge instead of ~68px in.
                          md: effectiveBallotOpen
                            ? '8px !important'
                            : onToggleBallot
                              ? '60px !important'
                              : '16px !important',
                        },
                        backgroundColor: `${primitiveColors.blue1} !important`,
                        alignItems: 'center !important',
                        borderBottom: 'none !important',
                        boxShadow: 'none !important',
                      },
                    }),
                // Always hide built-in menu buttons to prevent conflicts. The
                // header account avatar ("Account menu") opts out by class.
                '& .MuiIconButton-root[aria-label*="menu"]:not(.vm-header-account)': {
                  display: 'none !important',
                },
                '& .MuiIconButton-root[aria-label*="Menu"]:not(.vm-header-account)': {
                  display: 'none !important',
                },
                // Hide Toolpad's built-in account/avatar button in the AppBar
                '& .MuiToolbar-root button[aria-label="Account"]': {
                  display: 'none !important',
                },
                // RTL: Toolpad's toolbar actions Stack has `marginLeft: auto`
                // which in an RTL flex context pushes the controls toward the
                // logo instead of toward the far edge. Override with
                // `marginRight: auto` so the group is flush with the opposite
                // edge from the logo, matching LTR behavior.
                ...(dir === 'rtl'
                  ? {
                      '& .MuiToolbar-root .MuiStack-root > .MuiStack-root:last-child': {
                        marginLeft: '0 !important',
                        marginRight: 'auto !important',
                      },
                    }
                  : {}),
              }}
            >
              {children}
            </DashboardLayout>

            {showFeedbackChip && <FeedbackSurveyChip />}

            {/* Ballot Panel — desktop only. On mobile, the ballot lives
            inside BallotSheet (bottom sheet) which is rendered below
            this block when the layout has a ballot toggle. */}
            {onToggleBallot && (
              <Box
                role="region"
                // Align with the inner h1 "Your VoteMate Ballot Preview" so
                // landmark navigation and the heading announce consistently
                // (JAWS will announce the region label then immediately
                // encounter the h1; matching avoids a confusing label flip).
                aria-label={t('ballotPreviewLabel')}
                sx={{
                  // ``md`` here is ``demoTheme``'s 600px, not the app theme's
                  // 900px, because this tree renders inside Toolpad's provider.
                  // That is deliberate: ``BallotSheet`` hides itself at the same
                  // 600px, so the pane picks the ballot up exactly where the
                  // sheet drops it. Raising this to ``isMobile`` (900px) leaves
                  // 600-899 with no ballot UI at all — the width a partner
                  // embed typically renders at.
                  display: {
                    xs: 'none', // Mobile: replaced by BallotSheet
                    md: 'block', // Desktop: visible
                  },
                  position: {
                    xs: 'fixed', // Mobile: fixed positioning when controlled by context
                    md: 'fixed', // Desktop: fixed positioning
                  },
                  top: {
                    xs: bannerHeight, // Mobile: offset by notification banner
                    md: bannerHeight, // Desktop: offset by notification banner
                  },
                  ...(dir === 'rtl'
                    ? { left: { xs: 0, md: 35 }, right: 'auto' }
                    : { right: { xs: 0, md: 35 } }),
                  width: {
                    xs: effectiveBallotOpen ? '100vw' : '0', // Mobile: full viewport width when open
                    md: effectiveBallotOpen ? 'calc(50% - 35px)' : '0', // Desktop: adjust width based on ballot state
                  },
                  height: {
                    xs: `calc(100dvh - ${bannerHeight}px)`, // Mobile: dynamic viewport — tracks URL-bar collapse so the panel doesn't extend below the visible area.
                    md: `calc(100vh - ${bannerHeight}px)`, // Desktop: full viewport height minus banner
                  },
                  backgroundColor: semanticColors.surface,
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  borderRadius: {
                    xs: '0 0 20px 20px', // Mobile: 20px radius on bottom corners only
                    md: '0', // Desktop: no border radius
                  },
                  zIndex: {
                    xs: 1800, // Mobile: above everything including left menu
                    md: 1100, // Desktop: above other content
                  },
                  boxShadow: {
                    xs: effectiveBallotOpen ? `0 -2px 8px ${semanticColors.shadowLight}` : 'none',
                    md: effectiveBallotOpen
                      ? dir === 'rtl'
                        ? `2px 0 8px ${semanticColors.shadowLight}`
                        : `-2px 0 8px ${semanticColors.shadowLight}`
                      : 'none',
                  },
                  flex: {
                    xs: effectiveBallotOpen ? '0 0 100dvh' : '0 0 0%', // Mobile: dvh so the flex basis matches the visible viewport, not the URL-bar-hidden one
                    md: 'unset', // Desktop: no flex
                  },
                }}
              >
                {effectiveBallotOpen && (
                  <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    {/* Persistent live region (stable across the landing↔ballot
                        swap below) so screen readers hear when a ballot starts
                        building after an address submit — the skeleton's own
                        status node is inside the swapping subtree and can't
                        announce a change it's mounted with. */}
                    <Box
                      role="status"
                      aria-live="polite"
                      aria-atomic="true"
                      sx={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        overflow: 'hidden',
                        clip: 'rect(0 0 0 0)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ballotBuilding ? t('ballot:loading.building') : ''}
                    </Box>
                    {/* Three states:
                      1. Unauthenticated + no cached address → embed the
                         guest landing page so the user can enter their
                         address and immediately get personalized data.
                         Replaces the national-ballot snapshot view that
                         used to render here (the snapshot is showy but
                         not actionable; the landing's address input
                         converts the guest in one step).
                      2. Has data path (authed + setup OR guest with
                         cached address) → SampleBallotView mode='personal'.
                      3. Authed without setup-complete (signup in progress) with
                         no address yet → the landing (address entry) via
                         showGuestLandingInPane; a cached guest address instead
                         keeps its ballot visible. */}
                    {showGuestLandingInPane ? (
                      <GuestLandingPage />
                    ) : (
                      <SampleBallotView
                        mode={canLoadRealBallot ? 'personal' : 'national'}
                        downloadRef={ballotDownloadRef}
                        onIsDownloadingChange={setIsBallotDownloading}
                      />
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Right-side ballot menu - hidden on mobile when controlled by context */}
            {onToggleBallot && (
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <BallotMenu
                  isOpen={ballotOpen}
                  onToggle={onToggleBallot}
                  onDownload={
                    canLoadRealBallot
                      ? () => ballotDownloadRef.current?.()
                      : guestDownload && (() => guestDownload('header'))
                  }
                  onSurvey={openSurveyPanel}
                  isDownloading={isBallotDownloading}
                  notificationHeight={bannerHeight}
                />
              </Box>
            )}

            {/* preview-end */}
          </Box>{' '}
          {/* Close flex container */}
          {/* Mobile-only bottom sheet ballot. The sheet's own teal
            header is the tap-to-open affordance when closed (no
            separate banner element). Renders as position:fixed
            siblings — see provider doc for the inline-render
            rationale (no portal). Gated to mobile + ballot-aware
            layouts only; never paints on desktop. */}
          {isMobile && onToggleBallot && navMode === 'sheet' && !signupPending && (
            <>
              <SafeAreaFiller />
              {/* No dimming backdrop — the chat stays fully visible and
                  scrollable behind the open sheet (parity with the mobile app). */}
              <BallotSheet mode={canLoadRealBallot ? 'personal' : 'national'} />
              {/* Replaces the BallotSheet's previewClosed treatment for
                guest + no-address + new-chat. ``MobileAddressBar``
                self-gates on ``closedDetentFrac > 0`` so it renders
                only in that scenario; otherwise it's a no-op. */}
              <MobileAddressBar />
            </>
          )}
          {/* Tab-bar nav mode: a bottom tab bar (Chat · Ballot · My Info) with a
              full-screen ballot/info panel in place of the draggable sheet.
              Stood down during signup for the same reason as the sheet above:
              there is no ballot until the address is saved, so the panel would
              mount a hidden guest landing behind the modal. ``MobileTabBar``
              also self-hides via ``shouldHideMobileTabBar``; gating here keeps
              the panel from paying for a render nobody sees. */}
          {isMobile && onToggleBallot && navMode === 'tab' && !signupPending && (
            <>
              <MobileBallotPanel
                mode={canLoadRealBallot ? 'personal' : 'national'}
                showGuestLanding={showGuestLandingInPane}
                ballotBuilding={ballotBuilding}
              />
              <MobileTabBar />
            </>
          )}
          {/* Page-scroll routes (Help/Settings) have no SafeAreaFiller; tint the
              canvas blue1 so the bottom safe area matches the surface. */}
          {isMobile && pageScroll && <CanvasBackground color={primitiveColors.blue1} />}
        </RailHoverContext.Provider>
      </BallotSheetProvider>
    </ReactRouterAppProvider>
  );
};
